import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import InvoiceReport from '../components/InvoiceReport';

const statusColors = {
    Paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Partial: 'bg-orange-100 text-orange-800 border-orange-200',
    Unpaid: 'bg-red-100 text-red-800 border-red-200',
    Refunded: 'bg-purple-100 text-purple-800 border-purple-200'
};

const InvoiceArchive = () => {
    const { authFetch, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    const [invoices, setInvoices] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        invoice_number: '',
        start_date: '',
        end_date: '',
        cashier_id: 'All',
        customer_id: 'All',
        depo_id: selectedDepo,
        status: 'All'
    });

    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    
    const [isPrintOpen, setIsPrintOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    useEffect(() => {
        authFetch('/api/users').then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : []));
        authFetch('/api/customers').then(r => r.json()).then(d => setCustomers(Array.isArray(d) ? d : []));
    }, [authFetch]);

    useEffect(() => {
        fetchAll();
    }, [filters, authFetch]);

    const fetchAll = () => {
        let url = '/api/invoices';
        const params = new URLSearchParams();
        if (filters.invoice_number) params.append('invoice_number', filters.invoice_number);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.cashier_id !== 'All') params.append('cashier_id', filters.cashier_id);
        if (filters.customer_id !== 'All') params.append('customer_id', filters.customer_id);
        if (filters.depo_id !== 'Global') params.append('depo_id', filters.depo_id);
        if (filters.status !== 'All') params.append('status', filters.status);
        
        if (params.toString()) url += `?${params.toString()}`;

        authFetch(url).then(r => r.json()).then(d => setInvoices(Array.isArray(d) ? d : []));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const columns = [
        { header: t('Invoice No'), accessor: 'invoice_number', render: r => <span className="font-mono text-gray-500 font-bold">{r.invoice_number}</span> },
        { header: t('Date'), accessor: 'created_at', render: r => new Date(r.created_at).toLocaleString() },
        { header: t('Customer'), accessor: 'customer_name', render: r => r.customer_name || t('Walk-in Customer') },
        { header: t('Cashier'), accessor: 'cashier_name', render: r => r.cashier_name || 'System' },
        { header: t('Total'), accessor: 'total_amount', render: r => `CFA${parseFloat(r.total_amount).toFixed(2)}` },
        { header: t('Final Amount'), accessor: 'total_ttc', render: r => <span className="font-bold text-indigo-700">CFA{parseFloat(r.total_ttc).toFixed(2)}</span> },
        {
            header: t('Status'), accessor: 'status', render: r => (
                <span className={`px-2 py-1 rounded-full text-xs font-bold border ${statusColors[r.status] || 'bg-gray-100'}`}>{t(r.status)}</span>
            )
        },
        {
            header: t('Actions'), accessor: 'actions', render: r => (
                <div className="flex gap-2">
                    {/* View / Print */}
                    <button onClick={() => { setSelectedInvoice(r); setIsPrintOpen(true); }}
                        className="text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1 rounded transition-colors text-sm font-semibold flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {t('View & Print')}
                    </button>
                </div>
            )
        },
    ];

    return (
        <div className="space-y-6">
            <header>
                <h2 className="text-3xl font-bold text-gray-800">{t('Invoice Archive')}</h2>
                <p className="text-gray-500 mt-1">{t('Search any invoice ever created in the system.')}</p>
            </header>

            {/* Filter Section */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Invoice Number')}</label>
                    <input type="text" value={filters.invoice_number} onChange={e => handleFilterChange('invoice_number', e.target.value)} className="w-full border p-2 rounded text-sm" placeholder="e.g. INV-1001" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Start Date')}</label>
                    <input type="date" value={filters.start_date} onChange={e => handleFilterChange('start_date', e.target.value)} className="w-full border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('End Date')}</label>
                    <input type="date" value={filters.end_date} onChange={e => handleFilterChange('end_date', e.target.value)} className="w-full border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Cashier')}</label>
                    <select value={filters.cashier_id} onChange={e => handleFilterChange('cashier_id', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Cashiers')}</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Customer')}</label>
                    <select value={filters.customer_id} onChange={e => handleFilterChange('customer_id', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Customers')}</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Status')}</label>
                    <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Status')}</option>
                        <option value="Paid">{t('Paid')}</option>
                        <option value="Partial">{t('Partial')}</option>
                        <option value="Unpaid">{t('Unpaid')}</option>
                        <option value="Refunded">{t('Refunded')}</option>
                    </select>
                </div>
            </div>

            <DataTable columns={columns} data={invoices} />

            {/* View Modal */}
            {isPrintOpen && selectedInvoice && (
                <InvoiceReport invoice={selectedInvoice} onClose={() => setIsPrintOpen(false)} />
            )}
        </div>
    );
};

export default InvoiceArchive;
